import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLazyGetChildAnalysisQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import { showSnackbar } from '../../../../redux/snackbarSlice';
import { getTypeColor, getTypeIcon } from '../../../../utils/nodeTypeMapper';

import { DateTime } from 'luxon';
import EnergyTreeNodeAutocomplete from '@/features/energyxplorer/components/EnergyTreeNodeAutocomplete';
import EnergyAnalyzerFilterDialog from '@/features/energyxplorer/components/EnergyAnalyzerFilterDialog';
import ChildAnalysisChart from '@/components/highcharts/powerAnalyser/ChildAnalysisChart';
import TimestampSummaryWarningIcon from '@/features/energyxplorer/components/TimestampsSummaryWarningIcon';
import {
  combineTimestampsSummaryList,
  convertTimestampsSummaryToDateTimes,
} from '@/features/energyxplorer/utils/timestampsSummaryHelpers';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const ChildAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  setTimestampSummary,
  setTimestampSummarySetBy,
  timeRange,
}) => {
  const dispatch = useDispatch();

  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const getFilterLabel = (type, value) => {
    switch (type) {
      case 'activeHoursFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_hours');
          case false:
            return t('filter_labels.inactive_hours');
          default:
            return t('filter_labels.all_hours');
        }
      case 'activeDaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_days');
          case false:
            return t('filter_labels.inactive_days');
          default:
            return t('filter_labels.all_days');
        }
      case 'activeHolidaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.holidays_included');
          case false:
            return t('filter_labels.holidays_excluded');
          default:
            return '';
        }
      default:
        return '';
    }
  };

  const [
    getChildAnalysis,
    {
      data: childAnalysisData,
      isLoading: isChildAnalysisLoading,
      isFetching: isChildAnalysisFetching,
      isError,
      error,
    },
  ] = useLazyGetChildAnalysisQuery();

  const [nodeToAnalyse, setNodeToAnalyse] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [data, setData] = useState([]);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [seriesColors, setSeriesColors] = useState([]);
  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);
  const [hiddenChildrenIds, setHiddenChildrenIds] = useState([]);
  const [dataSeriesAggregate, setDataSeriesAggregate] = useState({
    value: 'AverageEnergyUsage',
    label: t('child_analysis.avg'),
  });
  const anchorRef = useRef(null);
  const [openAggregationMenu, setOpenAggregationMenu] = useState(false);

  const options = [
    { value: 'TotalEnergyUsage', label: t('child_analysis.sum') },
    { value: 'AverageEnergyUsage', label: t('child_analysis.avg') },
    { value: 'MinEnergyUsage', label: t('child_analysis.min') },
    { value: 'MaxEnergyUsage', label: t('child_analysis.max') },
  ];

  const handleToggleAggregationMenu = () => {
    setOpenAggregationMenu((prev) => !prev);
  };

  const handleCloseAggregationMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenAggregationMenu(false);
  };
  const handleSelectAggregation = (option) => {
    setOpenAggregationMenu(false);
    setDataSeriesAggregate(option);
  };

  const filteredData = useMemo(() => {
    return data.map((entry) => ({
      ...entry,
      ChildNodesSummaries: entry.ChildNodesSummaries.filter(
        (child) => !hiddenChildrenIds.includes(child.Id),
      ),
    }));
  }, [data, hiddenChildrenIds]);

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  };
  const handleCloseFilterDialog = () => {
    setOpenFilterDialog(false);
  };

  const fetchChildAnalysis = async ({ start, end, filters }) => {
    try {
      const response = await getChildAnalysis({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        nodeId: nodeToAnalyse?.energyTreeNodeId,
        start,
        end,
        ...filters,
      }).unwrap();

      return {
        ...response,
        metadata: {
          ...filters,
          id: Date.now(),
        },
      };
    } catch (err) {
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl under hentning af data.',
          severity: 'error',
        }),
      );
    }
  };

  const handleApplyFilter = async (filters) => {
    const response = await fetchChildAnalysis({
      start: timeRange?.start,
      end: timeRange?.end,
      filters,
    });
    setData((prev) => [...prev, response]);
  };

  useEffect(() => {
    if (!nodeToAnalyse) return;

    const filterCombinations = [
      { activeHoursFilter: true, activeDaysFilter: true }, // Daytime + Weekday
      { activeHoursFilter: false, activeDaysFilter: true }, // Nighttime + Weekday
      { activeHoursFilter: true, activeDaysFilter: false }, // Daytime + Weekend
      { activeHoursFilter: false, activeDaysFilter: false }, // Nighttime + Weekend
    ];

    const fetchAllCombinations = async () => {
      const results = await Promise.all(
        filterCombinations.map(async (filters) => {
          try {
            const response = await fetchChildAnalysis({
              start: timeRange?.start,
              end: timeRange?.end,
              filters,
            });

            if (response?.TimestampsSummary) {
              // Set the timestamp summary for the first successful fetch
              setTimestampSummary(response.TimestampsSummary);
              setTimestampSummarySetBy('Neddelingsanalysen');
            } else {
              setTimestampSummary(null);
              setTimestampSummarySetBy(null);
            }

            return response;
          } catch (err) {
            console.error('Error fetching child analysis:', err);
            dispatch(
              showSnackbar({
                message: 'Der opstod en fejl under hentning af data.',
                severity: 'error',
              }),
            );
            return null; // Return null for failed fetches
          }
        }),
      );

      // Filter out failed fetches (null/undefined) before setting
      const successfulResults = results.filter(Boolean);

      setData(successfulResults);
    };

    fetchAllCombinations();
  }, [nodeToAnalyse, timeRange]);

  useEffect(() => {
    if (!nodeToAnalyse && selectedEnergyGroup?.energyTreeNodes.length > 0) {
      setNodeToAnalyse(selectedEnergyGroup?.energyTreeNodes[0]);
    }
  }, [selectedEnergyGroup]);

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();

    // First pass: add all nodes
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      map.set(node.energyTreeNodeId, { ...node, children: [] });
    });

    // Second pass: link children to parents
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      if (node.parentId && map.has(node.parentId)) {
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [],
    [nodeToAnalyse, nodeLookup],
  );
  const parent = useMemo(
    () => nodeLookup.get(nodeToAnalyse?.parentId) || null,
    [nodeToAnalyse, nodeLookup],
  );

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          item
          md={12}
          lg={filtersOpen ? 9 : 12}
          sx={{ transition: 'flex-basis 0.3s ease' }}
        >
          <Card variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
            <CardContent sx={{ minHeight: 400 }}>
              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {true && (
                        <>
                          <Typography
                            variant="h6"
                            color="textSecondary"
                            fontWeight="bold"
                          >
                            {nodeToAnalyse?.name || ''}
                          </Typography>
                          <TimestampSummaryWarningIcon
                            node={nodeToAnalyse}
                            includeChildrenInWarning={true}
                          />
                          <Tooltip
                            title={t('child_analysis.series_description')}
                            placement="right"
                          >
                            <InfoRoundedIcon
                              fontSize="small"
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'lightgray'
                                    : 'gray',
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </>
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {t('child_analysis.in_period')}{' '}
                      {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : t('child_analysis.select_period')}
                    </Typography>
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Tooltip
                      title={t('child_analysis.select_aggregation')}
                      placement="top"
                    >
                      <div>
                        <Button
                          ref={anchorRef}
                          onClick={handleToggleAggregationMenu}
                          startIcon={<FunctionsRoundedIcon />}
                          // disabled={aggregate === 'hour'}
                          sx={{
                            p: 1,
                            px: 2,
                            borderRadius: 3,
                            color: 'text.primary',
                            width: 'auto',
                            border: '1px solid',
                            borderColor: 'divider',
                            mr: 1,
                            textTransform: 'none', // optional: to keep label casing
                          }}
                        >
                          {dataSeriesAggregate?.label ?? 'Vælg'}
                        </Button>
                      </div>
                    </Tooltip>

                    <Popper
                      open={openAggregationMenu}
                      anchorEl={anchorRef.current}
                      placement="bottom-end"
                      transition
                      disablePortal
                      sx={{ zIndex: 1301 }}
                    >
                      {({ TransitionProps }) => (
                        <Grow
                          {...TransitionProps}
                          style={{ transformOrigin: 'left top' }}
                        >
                          <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{ mt: 0.5, borderRadius: 2 }}
                          >
                            <ClickAwayListener
                              onClickAway={handleCloseAggregationMenu}
                            >
                              <MenuList
                                autoFocusItem={open}
                                dense
                                sx={{ minWidth: 150, p: 1 }}
                              >
                                {options.map((option) => (
                                  <MenuItem
                                    key={option.value}
                                    onClick={() =>
                                      handleSelectAggregation(option)
                                    }
                                    sx={{
                                      py: '6px',
                                      px: '12px',
                                      borderRadius: '5px',
                                      '&:hover': {
                                        bgcolor: 'action.hover',
                                        transition: 'all 0.3s ease',
                                      },
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {option.label}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                    <Tooltip
                      className="non-printable"
                      title={`${filtersOpen ? t('child_analysis.hide_filters') : t('child_analysis.show_filters')}`}
                      placement="top"
                    >
                      <IconButton
                        onClick={() => setFiltersOpen(!filtersOpen)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 3,
                          ml: 4,
                        }}
                      >
                        {filtersOpen ? (
                          <KeyboardArrowRightRoundedIcon />
                        ) : (
                          <KeyboardArrowLeftRoundedIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Stack>
              <Suspense fallback={<div>Loading...</div>}>
                <ChildAnalysisChart
                  data={filteredData}
                  dataSeriesAggregate={dataSeriesAggregate}
                  setSeriesColors={setSeriesColors}
                  isLoading={isChildAnalysisLoading || isChildAnalysisFetching}
                />
              </Suspense>
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen && (
          <Grid className="non-printable" item md={12} lg={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            fontWeight="bold"
                          >
                            {t('child_analysis.series')}
                          </Typography>
                          <Tooltip
                            title={t('child_analysis.add_series_description')}
                            placement="top"
                          >
                            <InfoRoundedIcon
                              fontSize="small"
                              sx={{
                                color: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'lightgray'
                                    : 'gray',
                                ml: 1,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        <Button
                          onClick={handleOpenFilterDialog}
                          variant="text"
                          endIcon={<AddRoundedIcon />}
                          disableElevation
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            // backgroundColor: 'hsl(0, 0%, 20%)',
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'text.primary',
                            },
                          }}
                        >
                          {t('child_analysis.add_series')}
                        </Button>
                      </Box>

                      <List dense>
                        {data?.map((series, index) => {
                          const seriesColor =
                            seriesColors[index]?.color ?? '#ccc';

                          return (
                            <ListItem
                              key={series?.metadata?.id || index}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                mb: 1,
                                alignItems: 'center',
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  onClick={() => {
                                    const idToRemove = series?.metadata?.id;
                                    setData((prev) =>
                                      prev.filter(
                                        (s) => s.metadata?.id !== idToRemove,
                                      ),
                                    );
                                    setSeriesColors((prev) =>
                                      prev.filter(
                                        (_, i) =>
                                          data[i]?.metadata?.id !== idToRemove,
                                      ),
                                    );
                                  }}
                                >
                                  <DeleteRoundedIcon />
                                </IconButton>
                              }
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: seriesColor,
                                  mr: 2,
                                  mt: 0.5,
                                }}
                              />
                              <ListItemText
                                // primary={`${seu} - Serie ${index + 1}`}
                                primary={`${t('child_analysis.series')} ${index + 1}`}
                                secondary={
                                  <Typography variant="caption" component="div">
                                    <i>{t('child_analysis.used_filters')}: </i>
                                    {[
                                      getFilterLabel(
                                        'activeDaysFilter',
                                        series?.metadata?.activeDaysFilter,
                                      ),
                                      getFilterLabel(
                                        'activeHoursFilter',
                                        series?.metadata?.activeHoursFilter,
                                      ),
                                      getFilterLabel(
                                        'activeHolidaysFilter',
                                        series?.metadata?.activeHolidaysFilter,
                                      ),
                                    ]
                                      .filter(Boolean)
                                      .map((text, i) => (
                                        <span key={i}>
                                          {i > 0 && ', '}
                                          <strong>{text}</strong>
                                        </span>
                                      ))}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                      <Grid item xs>
                        <EnergyTreeNodeAutocomplete
                          nodeOptions={selectedEnergyGroup?.energyTreeNodes}
                          selectedNode={nodeToAnalyse}
                          onChange={(newValue) => {
                            if (newValue) setNodeToAnalyse(newValue);
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <IconButton
                          onClick={() => parent && setNodeToAnalyse(parent)}
                          disabled={!parent}
                          sx={{
                            p: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                          }}
                        >
                          <ArrowUpwardRoundedIcon />
                        </IconButton>
                      </Grid>
                    </Grid>

                    <List
                      dense
                      sx={{
                        // maxHeight: 425,
                        overflowY: 'auto',
                      }}
                      subheader={
                        <ListSubheader
                          component="div"
                          id="nested-list-subheader"
                        >
                          {t('child_analysis.breakdown_for')}{' '}
                          {nodeToAnalyse?.name}:
                        </ListSubheader>
                      }
                    >
                      {children.length > 0 ? (
                        children.map((child) => {
                          const isHidden = hiddenChildrenIds.includes(
                            child.energyTreeNodeId,
                          );
                          const hasChildren =
                            nodeLookup.get(child?.energyTreeNodeId)?.children
                              .length > 0;
                          return (
                            <ListItem
                              key={child.energyTreeNodeId}
                              disablePadding
                              secondaryAction={
                                <Tooltip
                                  title={
                                    isHidden
                                      ? t('child_analysis.show_series_in_graph')
                                      : t('child_analysis.hide_series_in_graph')
                                  }
                                >
                                  <IconButton
                                    edge="end"
                                    onClick={(e) => {
                                      e.stopPropagation(); // prevent triggering setNodeToAnalyse
                                      setHiddenChildrenIds((prev) =>
                                        isHidden
                                          ? prev.filter(
                                              (id) =>
                                                id !== child.energyTreeNodeId,
                                            )
                                          : [...prev, child.energyTreeNodeId],
                                      );
                                    }}
                                  >
                                    {isHidden ? (
                                      <VisibilityOffRoundedIcon />
                                    ) : (
                                      <VisibilityRoundedIcon />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              }
                            >
                              <ListItemButton
                                disabled={!hasChildren}
                                onClick={() => setNodeToAnalyse(child)}
                                sx={{ borderRadius: 2 }}
                              >
                                <ListItemIcon>
                                  <Avatar
                                    variant="round"
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        getTypeColor(child.type) || '#555',
                                      color: '#fff',
                                      height: 32,
                                      width: 32,
                                      fontSize: 16,
                                    }}
                                  >
                                    <i
                                      key={child.energyTreeNodeId}
                                      className={`fas ${getTypeIcon(child.type)}`}
                                    />
                                  </Avatar>
                                </ListItemIcon>
                                <ListItemText
                                  primary={child.name}
                                  secondary={child.type}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, textAlign: 'center', color: 'gray' }}
                        >
                          {t('child_analysis.no_further_breakdown')}
                        </Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>

      <Suspense fallback={<div>Loading...</div>}>
        <EnergyAnalyzerFilterDialog
          open={openFilterDialog}
          handleClose={handleCloseFilterDialog}
          handleApply={handleApplyFilter}
          selectedEnergyGroup={selectedEnergyGroup}
          startHour={selectedEnergyGroup?.options?.workingDayStart}
          endHour={selectedEnergyGroup?.options?.workingDayEnd}
        />
      </Suspense>
    </Box>
  );
};

export default ChildAnalysisTab;
